import { useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
  Loader2,
  MessageCircle,
  TrendingUp,
  Bot,
  Receipt,
  Layers,
  Headset,
  Users,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";
import MagneticButton from "@/components/hero/MagneticButton";
import { cn } from "@/lib/utils";
import { useSiteSettings } from "@/hooks/use-site-settings";
import { displayPhone } from "@/lib/site-settings";

const NEEDS = [
  {
    id: "Growth & Tráfego",
    label: "Growth & Tráfego",
    icon: TrendingUp,
    placeholder: "Conte sobre suas campanhas, metas de crescimento e onde quer acelerar...",
  },
  {
    id: "Automação & IA",
    label: "Automação & IA",
    icon: Bot,
    placeholder: "Quais processos você quer automatizar? Atendimento, vendas, operações...",
  },
  {
    id: "Sistemas PDV/Finanças",
    label: "Sistemas PDV/Finanças",
    icon: Receipt,
    placeholder: "Fale sobre sua operação de PDV, fluxo de caixa e gestão financeira...",
  },
  {
    id: "Ecossistema Completo",
    label: "Ecossistema Completo",
    icon: Layers,
    placeholder: "Descreva seu negócio e como podemos unir marketing, IA e sistemas...",
  },
  {
    id: "Gerenciamento de Redes Sociais",
    label: "Gerenciamento de Redes Sociais",
    icon: Users,
    placeholder: "Fale sobre suas redes sociais, frequência de postagem e objetivos de engajamento...",
  },
] as const;

const formSchema = z.object({
  need: z.string().min(1, "Selecione o que sua empresa precisa"),
  name: z.string().trim().min(1, "Nome completo é obrigatório").max(120),
  phone: z.string().trim().min(1, "WhatsApp é obrigatório").refine(
    (v) => v.replace(/\D/g, "").length >= 10,
    "Informe um WhatsApp válido com DDD",
  ),
  email: z.union([z.string().trim().email("E-mail inválido").max(255), z.literal("")]).optional(),
  company: z.string().trim().max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  website: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const WHATSAPP_MESSAGE = "Olá! Acabei de solicitar um orçamento no site.";
const WHATSAPP_SUPPORT_MESSAGE = "Olá! Vim pelo site da Abillyting e gostaria de falar com a equipe.";

// Focus glow com as cores da marca (dourado)
const glowField =
  "transition-all duration-200 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:shadow-[0_0_24px_hsl(var(--primary)/0.25)]";

const Contact = () => {
  const { settings } = useSiteSettings();
  const WHATSAPP_NUMBER = settings.whatsapp_primary;
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const formStartTime = useRef(Date.now());

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      need: "",
      name: "",
      phone: "",
      email: "",
      company: "",
      description: "",
      website: "",
    },
    mode: "onBlur",
  });

  const selectedNeed = watch("need");
  const needConfig = NEEDS.find((n) => n.id === selectedNeed);

  const onSubmit = async (data: FormData) => {
    // Anti-spam: honeypot
    if (data.website) {
      console.log("Spam detected");
      return;
    }

    // Anti-spam: tempo mínimo de preenchimento
    if (Date.now() - formStartTime.current < 3000) {
      toast({
        title: "Erro",
        description: "Por favor, preencha o formulário com calma.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: response, error } = await supabase.functions.invoke("submit-lead", {
        body: {
          name: data.name,
          phone: data.phone,
          email: data.email,
          company: data.company,
          description: data.description,
          need: data.need,
          website: data.website,
        },
      });

      if (error) {
        const details = error instanceof FunctionsHttpError ? await error.context.text() : error.message;
        console.error("[submit-lead] Erro:", details);
        throw new Error("Falha ao enviar solicitação");
      }

      if (!response?.success) {
        throw new Error(response?.error || "Falha ao enviar solicitação");
      }

      setSubmitted(true);
      reset();
    } catch (err) {
      console.error("Erro ao enviar formulário:", err);
      toast({
        title: "Erro ao enviar",
        description: "Não foi possível enviar sua solicitação. Tente novamente ou fale pelo WhatsApp.",
        variant: "destructive",
      });
    }
  };

  return (
    <section id="contato" className="py-24 bg-section-bg relative overflow-hidden">
      {/* Decorative glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl"
      />
      <div className="container mx-auto px-4 relative">
        <div className="max-w-5xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4 animate-fade-in">
            <span className="inline-block text-primary font-medium text-sm tracking-widest uppercase">Fale conosco</span>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground text-balance">
              Diagnóstico <span className="bg-hero-gradient bg-clip-text text-transparent">da sua marca</span>
            </h2>
            <div className="w-20 h-1 bg-hero-gradient mx-auto rounded-full" />
            <p className="text-xl text-muted-foreground pt-2">
              Conte o que sua empresa precisa e receba um plano sob medida.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info + Suporte Rápido */}
            <div className="space-y-6">
              {/* WhatsApp Quick Support */}
              <Card className="relative overflow-hidden border-primary/30 bg-card/80 backdrop-blur-sm">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary/10 blur-2xl"
                />
                <CardHeader className="relative">
                  <div className="inline-flex items-center gap-2 self-start rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary mb-2">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#25D366]" />
                    </span>
                    Agentes &amp; Equipe Online
                  </div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Headset className="text-primary" size={22} />
                    Suporte Rápido
                  </CardTitle>
                  <CardDescription>
                    Prefere conversar agora? Nossa equipe responde em minutos pelo WhatsApp.
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative">
                  <MagneticButton
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_SUPPORT_MESSAGE)}`}
                    fullWidth
                    className="w-full bg-[#25D366] hover:bg-[#1EBE5B] text-white hover:shadow-xl hover:shadow-[#25D366]/30"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    Chamar no WhatsApp
                  </MagneticButton>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold mb-1">Email</h3>
                      <p className="text-sm text-muted-foreground">
                        <a href={`mailto:${settings.email}`} className="break-all">{settings.email}</a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="text-accent" size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold mb-1">Telefone</h3>
                      <p className="text-sm text-muted-foreground">
                        {displayPhone(settings.whatsapp_primary)}<br />
                        {displayPhone(settings.whatsapp_secondary)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-primary" size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold mb-1">Localização</h3>
                      <p className="text-sm text-muted-foreground">
                        Atendimento 100% Online
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-hero-gradient text-primary-foreground">
                <CardHeader>
                  <CardTitle className="font-heading">Horário de Atendimento</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p>Segunda a Sexta: 9h às 18h</p>
                    <p>Sábado: 9h às 13h</p>
                    <p>Domingo: Fechado</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="font-heading text-2xl">Solicite um Orçamento</CardTitle>
                <CardDescription>
                  Preencha o diagnóstico abaixo e nossa equipe entrará em contato em breve
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center space-y-6 animate-fade-in">
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
                      <div className="relative w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                        <CheckCircle2 className="text-primary" size={40} />
                      </div>
                    </div>
                    <div className="space-y-2 max-w-md">
                      <h3 className="font-heading font-bold text-2xl">Solicitação enviada!</h3>
                      <p className="text-muted-foreground">
                        Recebemos seus dados. Nossa equipe entrará em contato pelo WhatsApp em breve.
                      </p>
                    </div>
                    <Button
                      asChild
                      className="bg-[#25D366] hover:bg-[#128C7E] text-white font-medium text-lg py-6 px-8"
                    >
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MessageCircle className="mr-2" size={20} />
                        Falar no WhatsApp agora
                      </a>
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Seletor de necessidade */}
                    <div className="space-y-3">
                      <Label className="text-base font-heading font-semibold">
                        O que sua empresa precisa hoje? *
                      </Label>
                      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Necessidade da empresa">
                        {NEEDS.map((need) => {
                          const active = selectedNeed === need.id;
                          const Icon = need.icon;
                          return (
                            <button
                              key={need.id}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              onClick={() =>
                                setValue("need", need.id, { shouldValidate: true, shouldDirty: true })
                              }
                              className={cn(
                                "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 hover:scale-[1.03] active:scale-95",
                                active
                                  ? "bg-hero-gradient text-primary-foreground border-transparent shadow-lg shadow-primary/25 animate-scale-in"
                                  : "border-border bg-background/60 text-muted-foreground hover:border-primary/60 hover:text-primary hover:shadow-md hover:shadow-primary/10",
                              )}
                            >
                              <Icon size={16} className={active ? "text-primary-foreground" : "text-primary"} />
                              {need.label}
                            </button>
                          );
                        })}
                      </div>
                      {errors.need && (
                        <p className="text-sm text-destructive">{errors.need.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome Completo *</Label>
                        <Input
                          id="name"
                          placeholder="Seu nome"
                          autoComplete="name"
                          aria-invalid={errors.name ? "true" : "false"}
                          className={cn(glowField, errors.name && "border-destructive focus-visible:ring-destructive")}
                          {...register("name")}
                        />
                        {errors.name && (
                          <p className="text-sm text-destructive">{errors.name.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">WhatsApp / Celular *</Label>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <Input
                              id="phone"
                              type="tel"
                              inputMode="tel"
                              placeholder="(41) 98869-2568"
                              autoComplete="tel"
                              aria-invalid={errors.phone ? "true" : "false"}
                              className={cn(glowField, errors.phone && "border-destructive focus-visible:ring-destructive")}
                              value={field.value}
                              onChange={(e) => field.onChange(formatPhone(e.target.value))}
                              onBlur={field.onBlur}
                            />
                          )}
                        />
                        {errors.phone && (
                          <p className="text-sm text-destructive">{errors.phone.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">E-mail</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="seu@email.com"
                          autoComplete="email"
                          aria-invalid={errors.email ? "true" : "false"}
                          className={cn(glowField, errors.email && "border-destructive focus-visible:ring-destructive")}
                          {...register("email")}
                        />
                        {errors.email && (
                          <p className="text-sm text-destructive">{errors.email.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company">Nome da Empresa</Label>
                        <Input
                          id="company"
                          placeholder="Nome da sua empresa"
                          autoComplete="organization"
                          className={glowField}
                          {...register("company")}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Conte um pouco mais</Label>
                      <Textarea
                        id="description"
                        placeholder={needConfig?.placeholder ?? "Conte-nos mais sobre seu projeto..."}
                        rows={3}
                        aria-invalid={errors.description ? "true" : "false"}
                        className={cn(glowField, errors.description && "border-destructive focus-visible:ring-destructive")}
                        {...register("description")}
                      />
                      {errors.description && (
                        <p className="text-sm text-destructive">{errors.description.message}</p>
                      )}
                    </div>

                    {/* Honeypot field - hidden from users */}
                    <input
                      type="text"
                      {...register("website")}
                      style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px" }}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      aria-label="Leave this field empty"
                    />

                    <MagneticButton
                      type="submit"
                      fullWidth
                      disabled={isSubmitting}
                      className="w-full"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        "Enviar Diagnóstico"
                      )}
                    </MagneticButton>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
